/**
 * Mission Default Values
 *
 * Default parameters and settings for mission planning
 */

import { MavCommand } from './mavlink-commands';
import { VehicleType, MissionStatus } from '@/features/mission/types/mission.types';
import { WaypointFrame } from '@/features/mission/types/waypoint.types';

/**
 * Default mission parameters
 */
export const MISSION_DEFAULTS = {
  // Altitude settings (meters)
  defaultAltitude: 50,           // Default altitude for new waypoints
  minAltitude: 0,                // Minimum safe altitude
  maxAltitude: 120,              // Maximum altitude (recreational limit)
  maxAltitudeKopasgat: 500,      // Maximum altitude for KOPASGAT operations

  // Speed settings (m/s)
  defaultSpeed: 5,               // Default cruise speed
  minSpeed: 1,                   // Minimum speed
  maxSpeed: 15,                  // Maximum speed for multicopter
  cruiseSpeed: 8,                // Typical cruise speed

  // Waypoint parameters
  defaultAcceptanceRadius: 3,    // Default acceptance radius (meters)
  defaultHoldTime: 0,            // Default hold time at waypoint (seconds)
  defaultYaw: 0,                 // Default yaw angle (degrees)

  // Vehicle type
  defaultVehicleType: VehicleType.COPTER,

  // Mission status
  initialStatus: MissionStatus.PLANNING,

  // Home position (Jakarta, Indonesia - will be updated based on user location)
  defaultHomePosition: {
    latitude: -6.2088,
    longitude: 106.8456,
    altitude: 0,
  },
} as const;

/**
 * Default waypoint settings
 */
export const WAYPOINT_DEFAULTS = {
  command: MavCommand.NAV_WAYPOINT,
  frame: WaypointFrame.GLOBAL_RELATIVE_ALT_INT,
  param1: 0,                      // Hold time
  param2: 3,                      // Acceptance radius
  param3: 0,                      // Pass radius
  param4: 0,                      // Yaw
  autocontinue: true,
  isCurrentWaypoint: false,
  isSelected: false,
  isHovered: false,
} as const;

/**
 * Validation limits
 */
export const VALIDATION_LIMITS = {
  // Coordinate limits
  minLatitude: -90,
  maxLatitude: 90,
  minLongitude: -180,
  maxLongitude: 180,

  // Distance limits (meters)
  maxWaypointDistance: 5000,     // Warn if waypoint > 5km from previous
  minWaypointDistance: 1,        // Warn if waypoints too close

  // Altitude limits (meters)
  minSafeAltitude: 5,            // Minimum safe altitude above ground
  maxSafeAltitude: 500,          // Maximum safe altitude for operations

  // Mission limits
  maxWaypoints: 500,             // Maximum waypoints in a mission
  minWaypoints: 2,               // Minimum waypoints (takeoff + land)

  // Battery reserve
  minBatteryReserve: 30,         // Minimum 30% battery reserve for RTL
} as const;

/**
 * Battery consumption estimates (% per meter)
 * Based on typical multicopter performance
 */
export const BATTERY_ESTIMATES = {
  [VehicleType.COPTER]: {
    horizontal: 0.02,              // 0.02% per meter horizontal
    vertical: 0.10,                // 0.10% per meter vertical (climb)
    descent: 0.03,                 // 0.03% per meter vertical (descent)
    hover: 0.5,                    // 0.5% per second hovering
  },
  [VehicleType.PLANE]: {
    horizontal: 0.01,
    vertical: 0.05,
    descent: 0.01,
    hover: 0,                      // Planes don't hover
  },
  [VehicleType.VTOL]: {
    horizontal: 0.015,
    vertical: 0.08,
    descent: 0.02,
    hover: 0.4,
  },
  [VehicleType.ROVER]: {
    horizontal: 0.005,
    vertical: 0,
    descent: 0,
    hover: 0.1,                    // Stationary power consumption
  },
  [VehicleType.SUB]: {
    horizontal: 0.01,
    vertical: 0.03,
    descent: 0.02,
    hover: 0.2,
  },
} as const;

/**
 * Visual settings for map rendering
 */
export const VISUAL_DEFAULTS = {
  // Waypoint marker colors (hex)
  waypointColorDefault: '#00FFFF',      // Cyan
  waypointColorSelected: '#FFFF00',     // Yellow
  waypointColorHovered: '#00FF00',      // Green
  waypointColorCurrent: '#FF00FF',      // Magenta
  waypointColorError: '#FF0000',        // Red

  // Flight path colors
  flightPathColor: '#00FFFF',           // Cyan
  flightPathColorActive: '#FF00FF',     // Magenta
  flightPathWidth: 3,                   // Line width (pixels)

  // Marker sizes
  waypointMarkerSize: 36,               // Default size (pixels)
  waypointMarkerSizeSelected: 48,       // Selected size (pixels)
  waypointMarkerSizeHovered: 42,        // Hovered size (pixels)
} as const;

/**
 * Auto-save settings
 */
export const AUTOSAVE_SETTINGS = {
  enabled: true,
  intervalSeconds: 30,           // Auto-save every 30 seconds
  maxHistoryStates: 50,          // Keep last 50 states for undo/redo
  maxAutoSaves: 10,              // Keep last 10 auto-saves
} as const;

/**
 * UI Settings
 */
export const UI_SETTINGS = {
  // List virtualization
  virtualScrollItemHeight: 60,   // Height of each waypoint list item (pixels)
  virtualScrollOverscan: 5,      // Number of items to render outside viewport

  // Map interaction
  clickToAddDelay: 200,          // Debounce delay for click-to-add (ms)
  dragUpdateThrottle: 50,        // Throttle drag updates (ms)

  // Keyboard shortcuts
  shortcuts: {
    delete: 'Delete',
    undo: 'Ctrl+Z',
    redo: 'Ctrl+Shift+Z',
    save: 'Ctrl+S',
    addWaypoint: 'A',
    escape: 'Escape',
  },
} as const;

/**
 * Export format settings
 */
export const EXPORT_SETTINGS = {
  // JSON export
  jsonIndent: 2,                 // Indentation for JSON files

  // KML export
  kmlAltitudeMode: 'relativeToGround',
  kmlLineWidth: 2,
  kmlLineColor: '7f00ffff',      // ABGR format (semi-transparent cyan)

  // File naming
  fileNamePrefix: 'Mission',
  fileNameDateFormat: 'yyyyMMdd_HHmmss',
} as const;
